import React, { useState } from 'react';
import { useNotifications } from './hooks/useNotifications';
import { cn } from '@/utils/cn';
import { Card, Button } from '@/components/ui';
import { Bell, Mail, Smartphone, Monitor, Moon, Clock, Save, Loader2 } from 'lucide-react';

export function NotificationsPage() {
  const { categories, isLoading, updateCategory } = useNotifications();
  const [isSaving, setIsSaving] = useState(false);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Cài đặt Thông báo</h3>
          <p className="text-xs text-slate-500">Tùy chỉnh cách thức và thời điểm nhận thông báo từ hệ thống Landscape.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Main Content */}
        <div className="xl:col-span-8 space-y-6">
          <Card className="bg-white border-2 border-slate-100 shadow-sm relative overflow-hidden rounded-3xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl border border-emerald-100 shrink-0">
                <Bell size={24} />
              </div>
              <div>
                <h4 className="text-slate-900 font-bold text-base tracking-tight">Kênh thông báo</h4>
                <p className="text-slate-500 text-xs mt-0.5">Lựa chọn nền tảng bạn muốn nhận cảnh báo theo từng chuyên mục</p>
              </div>
            </div>

            <div className="space-y-4">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="p-5 animate-pulse flex flex-col gap-3 border border-slate-100 rounded-2xl">
                    <div className="h-4 w-1/4 rounded bg-slate-100" />
                    <div className="h-3 w-1/2 rounded bg-slate-50" />
                  </div>
                ))
              ) : (
                categories.map((cat) => (
                  <div key={cat.id} className="p-5 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors border border-slate-100 rounded-2xl">
                    <div className="flex-1 max-w-md">
                      <h4 className="text-sm font-bold text-slate-800">{cat.name}</h4>
                      <p className="text-xs font-medium text-slate-500 mt-1.5 leading-relaxed">{cat.description}</p>
                    </div>
                    
                    <div className="flex items-center gap-4 bg-white p-2.5 rounded-2xl border border-slate-100 shadow-sm shrink-0">
                      <ChannelToggle 
                        icon={<Monitor size={18} />}
                        label="Hệ thống" 
                        active={cat.channels.inApp} 
                        onChange={(v) => updateCategory({ categoryId: cat.id, channels: { ...cat.channels, inApp: v } })} 
                      />
                      <div className="w-px h-8 bg-slate-100" />
                      <ChannelToggle 
                        icon={<Smartphone size={18} />}
                        label="Ứng dụng" 
                        active={cat.channels.push} 
                        onChange={(v) => updateCategory({ categoryId: cat.id, channels: { ...cat.channels, push: v } })} 
                      />
                      <div className="w-px h-8 bg-slate-100" />
                      <ChannelToggle 
                        icon={<Mail size={18} />}
                        label="Email" 
                        active={cat.channels.email} 
                        onChange={(v) => updateCategory({ categoryId: cat.id, channels: { ...cat.channels, email: v } })} 
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <aside className="xl:col-span-4 space-y-6">
           <Card className="p-6 bg-white border-2 border-slate-100 shadow-sm relative overflow-hidden rounded-3xl">
              <div className="flex items-center gap-4 mb-5">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100 shrink-0">
                  <Moon size={20} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-sm tracking-tight">Thời gian yên tĩnh</h4>
                  <p className="text-slate-500 text-[11px] mt-0.5">Tạm dừng thông báo Push/Email</p>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mb-6">
                Chế độ này sẽ chặn tất cả thông báo gửi đến điện thoại hoặc email của bạn trong khoảng thời gian thiết lập. Rất hữu ích khi bạn đang nghỉ phép hoặc ngủ.
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 transition-colors rounded-xl border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-xs font-bold">Từ giờ</span>
                  </div>
                  <input type="time" defaultValue="22:00" className="text-sm font-black text-slate-800 bg-transparent outline-none cursor-pointer" />
                </div>
                <div className="flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 transition-colors rounded-xl border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-xs font-bold">Đến giờ</span>
                  </div>
                  <input type="time" defaultValue="06:00" className="text-sm font-black text-slate-800 bg-transparent outline-none cursor-pointer" />
                </div>
              </div>

              <Button 
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                onClick={() => {
                  setIsSaving(true);
                  setTimeout(() => setIsSaving(false), 500);
                }}
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} className="mr-2" />}
                Lưu lịch trình
              </Button>
           </Card>
        </aside>
      </div>
    </div>
  );
}

function ChannelToggle({ icon, label, active, onChange }: { icon: React.ReactNode; label: string; active: boolean; onChange: (v: boolean) => void }) {
  return (
    <div 
      className="flex flex-col items-center gap-1.5 group cursor-pointer w-16"
      onClick={() => onChange(!active)}
    >
      <div className={cn(
        "flex flex-col items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 relative",
        active ? "bg-emerald-50 text-emerald-600 shadow-sm border border-emerald-100 transform scale-105" : "text-slate-400 hover:bg-slate-50 border border-transparent"
      )}>
        {icon}
        {active && (
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
        )}
      </div>
      <span className={cn(
        "text-[10px] font-bold transition-colors",
        active ? "text-emerald-700" : "text-slate-400 group-hover:text-slate-600"
      )}>
        {label}
      </span>
    </div>
  );
}
